import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ThumbsUp, ThumbsDown, MessageCircle, CheckCheck, X } from "lucide-react";

// Sprint 8.F — Inline-Feedback-Trigger (Post-Flexpfad + Inaktivität).
// Anti-Spam: post_bruecke max 1× pro Session, inactivity max 1× pro Tag/URL.
// Erscheint NICHT im Read-Only-Modus.

const VARIANTS = {
  post_bruecke: {
    sessionKey: "f4s_fb_post_bruecke_shown",
    storage: "session",
    headline: "Hat das funktioniert?",
    subtitle: "Dein Flexpfad wurde gespeichert. Hat alles geklappt?",
    yesLabel: "Ja",
    YesIcon: ThumbsUp,
    noLabel: "Nein",
    NoIcon: ThumbsDown,
    yesToast: "Schön, dass es geklappt hat! 🎉",
    bugContext: "bruecke_save",
    questionFlow: false,
  },
  inactivity: {
    sessionKey: "f4s_fb_inactivity_shown",
    storage: "local",
    headline: "Brauchst du Hilfe?",
    subtitle: "Du bist seit einer Weile auf dieser Seite — sollen wir dir helfen?",
    yesLabel: "Ja, hilf mir",
    YesIcon: MessageCircle,
    noLabel: "Alles gut",
    NoIcon: CheckCheck,
    yesToast: null,
    bugContext: null,
    questionFlow: true,
  },
};

function readMarker(variant) {
  try {
    const meta = VARIANTS[variant];
    if (meta.storage === "session") {
      return sessionStorage.getItem(meta.sessionKey) === "1";
    }
    const day = new Date().toISOString().slice(0, 10);
    return localStorage.getItem(`${meta.sessionKey}_${day}_${window.location.pathname}`) === "1";
  } catch (_) {
    return false;
  }
}

function writeMarker(variant) {
  try {
    const meta = VARIANTS[variant];
    if (meta.storage === "session") {
      sessionStorage.setItem(meta.sessionKey, "1");
      return;
    }
    const day = new Date().toISOString().slice(0, 10);
    localStorage.setItem(`${meta.sessionKey}_${day}_${window.location.pathname}`, "1");
  } catch (_) { /* noop */ }
}

export default function InlineFeedbackTrigger({
  variant,           // 'post_bruecke' | 'inactivity'
  show,              // controlled visibility (from parent)
  onShown,           // called once when first rendered
  onDismiss,
  isReadOnly = false,
  context,
}) {
  const navigate = useNavigate();
  const meta = VARIANTS[variant];
  const [visible, setVisible] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!show || !meta) return;
    if (isReadOnly) return;
    if (readMarker(variant)) return;
    setVisible(true);
    writeMarker(variant);
    if (onShown) onShown();
  }, [show, variant, isReadOnly, meta, onShown]);

  if (!meta) return null;

  const close = () => {
    setVisible(false);
    if (onDismiss) onDismiss();
  };

  const handleYes = () => {
    if (meta.questionFlow) {
      const ctx = encodeURIComponent(context?.url || window.location.pathname);
      navigate(`/mitgestalten?type=question&context=${ctx}`);
      close();
      return;
    }
    setToast(meta.yesToast);
    close();
    setTimeout(() => setToast(null), 3000);
  };

  const handleNo = () => {
    if (meta.bugContext) {
      const params = new URLSearchParams({
        type: "bug",
        context: meta.bugContext,
        ...(context?.bruecke_id ? { bruecke_id: context.bruecke_id } : {}),
      });
      navigate(`/mitgestalten?${params.toString()}`);
      close();
      return;
    }
    setToast("Schön! 👍");
    close();
    setTimeout(() => setToast(null), 2000);
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            data-testid={`inline-feedback-${variant}`}
            className="fixed bottom-6 right-6 z-40 max-w-sm w-[calc(100%-3rem)] sm:w-96"
          >
            <div className="rounded-xl bg-white shadow-2xl border border-slate-200 p-4 relative">
              <button
                type="button"
                onClick={close}
                aria-label="Schließen"
                data-testid={`inline-feedback-${variant}-close`}
                className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"
              >
                <X className="w-4 h-4" />
              </button>
              <p className="pr-6 text-sm font-bold text-[var(--fts-text-primary)]">
                {meta.headline}
              </p>
              <p className="mt-1 text-xs text-slate-600">{meta.subtitle}</p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={handleYes}
                  data-testid={`inline-feedback-${variant}-yes`}
                  className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-emerald-100 hover:bg-emerald-200 text-emerald-900 px-3 py-1.5 text-xs font-bold"
                >
                  <meta.YesIcon className="w-3.5 h-3.5" />
                  {meta.yesLabel}
                </button>
                <button
                  type="button"
                  onClick={handleNo}
                  data-testid={`inline-feedback-${variant}-no`}
                  className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-900 px-3 py-1.5 text-xs font-bold"
                >
                  <meta.NoIcon className="w-3.5 h-3.5" />
                  {meta.noLabel}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            data-testid={`inline-feedback-${variant}-toast`}
            className="fixed bottom-6 right-6 z-40 rounded-xl bg-slate-900 text-white px-4 py-2.5 text-sm font-bold shadow-2xl"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
